import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    IonContent,
    IonButton,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonModal,
    IonFooter,
    IonIcon,
    IonLabel,
    IonButtons, IonRange
} from "@ionic/react";
import { closeOutline, cloudUploadOutline } from "ionicons/icons";

const AvatarCropModal = ({rawImage,editorOpen,setEditorOpen,doCropAndUpload,imgEl,offset,setOffset,setZoom,zoom,setFitMode,fitMode,frameSize}) => {
    // Editor controls
    const dragRef = useRef({ dragging: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });

    // ===== helpers =====
    const getScale = (nW, nH, fW, fH, zoomVal) => {
        // cover always
        const sx = fW / (nW || 1);
        const sy = fH / (nH || 1);
        return Math.max(sx, sy) * (zoomVal || 1);
    };

// Clamp in *visual translate* space so tx,ty ∈ [frame - draw, 0]
// This lets you reach 0 (top/left) and pan both directions.
    const clampOffsetVisual = (ox, oy, drawW, drawH, frameW, frameH, centeredX, centeredY) => {
        const txMin = frameW - drawW, txMax = 0;
        const tyMin = frameH - drawH, tyMax = 0;

        const offXMin = txMin - centeredX, offXMax = txMax - centeredX;
        const offYMin = tyMin - centeredY, offYMax = tyMax - centeredY;

        let x = ox, y = oy;
        x = Math.min(offXMax, Math.max(offXMin, x));
        y = Math.min(offYMax, Math.max(offYMin, y));
        return { x, y };
    };

// ===== drag handlers =====
    const onPointerDown = (e) => {
        const pt = ('touches' in e) ? e.touches[0] : e;
        dragRef.current = {
            dragging: true,
            startX: pt.clientX,
            startY: pt.clientY,
            baseX: offset.x || 0,
            baseY: offset.y || 0,
        };
        e.currentTarget?.setPointerCapture?.(e.pointerId || 1);
    };

    const onPointerMove = (e) => {
        if (!dragRef.current?.dragging || !imgEl.current) return;

        const pt = ('touches' in e) ? e.touches[0] : e;
        const dx = pt.clientX - dragRef.current.startX;
        const dy = pt.clientY - dragRef.current.startY;

        const img = imgEl.current;
        const nW = img.naturalWidth || 1;
        const nH = img.naturalHeight || 1;

        const scale = getScale(nW, nH, frameSize, frameSize, zoom);
        const drawW = nW * scale;
        const drawH = nH * scale;

        const centeredX = (frameSize - drawW) / 2;
        const centeredY = (frameSize - drawH) / 2;

        const next = { x: (dragRef.current.baseX || 0) + dx, y: (dragRef.current.baseY || 0) + dy };
        const clamped = clampOffsetVisual(next.x, next.y, drawW, drawH, frameSize, frameSize, centeredX, centeredY);
        setOffset(clamped);
    };

    const onPointerUp = () => {
        if (dragRef.current) dragRef.current.dragging = false;
    };

// ===== displayStyles (cover always, matrix transform, fixed intrinsic size) =====
    const displayStyles = useMemo(() => {
        const img = imgEl.current;
        if (!img) return {};

        const nW = img.naturalWidth || 1;
        const nH = img.naturalHeight || 1;

        const scale = getScale(nW, nH, frameSize, frameSize, zoom); // cover
        const drawW = nW * scale;
        const drawH = nH * scale;

        const centeredX = (frameSize - drawW) / 2;
        const centeredY = (frameSize - drawH) / 2;

        const clamped = clampOffsetVisual(offset.x, offset.y, drawW, drawH, frameSize, frameSize, centeredX, centeredY);

        // expose for doCropAndUpload
        img.dataset.fit = 'cover'; // force cover
        img.dataset.zoom = String(zoom);
        img.dataset.offsetX = String(clamped.x);
        img.dataset.offsetY = String(clamped.y);
        img.dataset.centeredX = String(centeredX);
        img.dataset.centeredY = String(centeredY);
        img.dataset.drawW = String(drawW);
        img.dataset.drawH = String(drawH);

        const tx = centeredX + clamped.x;
        const ty = centeredY + clamped.y;

        return {
            width: `${nW}px`,
            height: `${nH}px`,
            maxWidth: 'none',
            maxHeight: 'none',
            objectFit: 'unset',
            transform: `matrix(${scale}, 0, 0, ${scale}, ${tx}, ${ty})`,
            transformOrigin: 'top left',
            willChange: 'transform',
        };
    }, [offset, zoom, frameSize]);

    const handleReady = () => {
        const img = imgEl?.current;
        const nW = img.naturalWidth || 1;
        const nH = img.naturalHeight || 1;

        // always use cover scaling
        const scale = getScale(nW, nH, frameSize, frameSize, zoom);
        const drawW = nW * scale;
        const drawH = nH * scale;

        // center image inside the square frame
        const centeredX = (frameSize - drawW) / 2;
        const centeredY = (frameSize - drawH) / 2;

        // reset offsets when a new image is loaded or window resized
        setOffset({ x: 0, y: 0 });

        // write base info to dataset (so doCropAndUpload reads correct defaults)
        img.dataset.centeredX = centeredX;
        img.dataset.centeredY = centeredY;
        img.dataset.drawW = drawW;
        img.dataset.drawH = drawH;
        img.dataset.fit = "cover";
        img.dataset.zoom = String(zoom);
        img.dataset.offsetX = "0";
        img.dataset.offsetY = "0";
    };


    return (
        <IonModal isOpen={editorOpen} onDidDismiss={() => setEditorOpen(false)} className="editor-modal adjust-image-in-box-modal">
            <IonHeader className={"sheet_modal_header"}>
                <IonToolbar>
                    <IonTitle className={"filter_sheet_header_text"}>Edit Profile Photo</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={() => setEditorOpen(false)} fill="clear"><IonIcon icon={closeOutline} /></IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
                <div className="editor-wrap">
                    <div
                        className="editor-frame"
                        style={{ width: frameSize, height: frameSize }}
                        onMouseDown={onPointerDown}
                        onMouseMove={onPointerMove}
                        onMouseUp={onPointerUp}
                        onMouseLeave={onPointerUp}
                        onTouchStart={onPointerDown}
                        onTouchMove={onPointerMove}
                        onTouchEnd={onPointerUp}
                    >
                        {rawImage?.dataUrl ? (
                            <img ref={imgEl} src={rawImage.dataUrl} onLoad={handleReady} alt="to-edit" className="editor-image" style={displayStyles} draggable={false} />
                        ) : (
                            <div className="editor-placeholder">Choose an image to start</div>
                        )}
                    </div>
                    <div className="editor-controls">
                        {/*<div className="control-row">*/}
                        {/*    <IonLabel>Fit</IonLabel>*/}
                        {/*    <div className="segmented">*/}
                        {/*        <button className={`seg-btn ${fitMode === 'contain' ? 'active' : ''}`} onClick={() => setFitMode('contain')}>Contain</button>*/}
                        {/*        <button className={`seg-btn ${fitMode === 'cover' ? 'active' : ''}`} onClick={() => setFitMode('cover')}>Cover</button>*/}
                        {/*    </div>*/}
                        {/*</div>*/}
                        <div className="control-row">
                            <IonLabel>Zoom</IonLabel>
                            <IonRange
                                min={1}
                                max={3}
                                pin
                                step={0.01}
                                value={zoom}
                                onIonChange={(e)=>setZoom(parseFloat(e.detail.value))}
                            />
                        </div>
                        <div className="hint">Tip: Drag the image to reposition inside the square.</div>
                    </div>
                </div>
            </IonContent>
            <IonFooter className="ion-padding">
                <IonToolbar>
                <IonButton expand="block" onClick={doCropAndUpload}>
                    <IonIcon slot="start" icon={cloudUploadOutline} />
                    Continue
                </IonButton>
                </IonToolbar>
            </IonFooter>

            {/* --- CUSTOM CSS (move to your stylesheet) --- */}
            <style>{`

        .adjust-image-in-box-modal .editor-modal .modal-wrapper{ --height:auto; }
        .adjust-image-in-box-modal .editor-wrap{ display:flex; flex-direction:column; gap:1rem; align-items:center; }
        .adjust-image-in-box-modal .editor-frame{ position:relative; overflow:hidden; border-radius:16px; background:#f4f6fb; box-shadow: inset 0 0 0 1px #e8ecff; touch-action:none; }
        .adjust-image-in-box-modal .editor-image{ position:absolute; transform-origin:center center; will-change:transform; }
        .adjust-image-in-box-modal .editor-placeholder{ width:100%; height:100%; display:grid; place-items:center; color:#8a93a6; }
        .adjust-image-in-box-modal .editor-controls{ width:100%; max-width:420px; display:flex; flex-direction:column; gap:.75rem; }
     .adjust-image-in-box-modal .control-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0px 12px;
    font-size: 1.2rem;
    color: #6b7280;
}
        .adjust-image-in-box-modal .segmented{ display:flex; background:#eef2ff; padding:4px; border-radius:10px; gap:4px; }
        .adjust-image-in-box-modal .seg-btn{ border:0; background:transparent; padding:.45rem .75rem; border-radius:8px; font-weight:600; color:#3b57f6; cursor:pointer; }
        .adjust-image-in-box-modal .seg-btn.active{ background:#fff; box-shadow:0 2px 10px rgba(0,0,0,.06); }
        .adjust-image-in-box-modal .hint{ font-size:.85rem; color:#6b7280; text-align:center; }

        /* Reuse your existing form styles; only minimal adjustments here */
        .adjust-image-in-box-modal .form-group{ margin-bottom: .9rem; }
        .adjust-image-in-box-modal .form-label{ display:block; margin-bottom:.35rem; font-weight:600; }
        .adjust-image-in-box-modal .form-input{ width:100%; border:1px solid #e5e7eb; border-radius:10px; padding:.7rem .8rem; font-size:1rem; background:#fff; }
        .adjust-image-in-box-modal .danger_error_text{ color:#ef4444; font-size:.85rem; margin-top:.25rem; }
      `}</style>
        </IonModal>
    );
};

export default AvatarCropModal;
